/**
 * Schema-Datei — provision a data model from an uploaded configuration file,
 * next to the built-in "Schema anlegen / ergänzen" button on the system console.
 *
 * The built-in AssetRadar model is compiled into the backend, which is why the
 * provisioning button takes no input. This card adds the operator's own model:
 * a YAML (or JSON) file that declares classes ("Karten"), their attributes, the
 * relations between them and the lookup lists. Two guard rails make it safe to
 * hand out:
 *
 *   - **Additive.** Provisioning only ever creates what CMDBuild is missing, so
 *     a file can add cards or extend an existing class, never rename or delete
 *     one. The SPA's own forms keep following the built-in model.
 *   - **Preview first.** "Nur prüfen" is checked by default: the server reads
 *     CMDBuild and reports what a real run would create, writing nothing. A
 *     malformed file answers with EVERY validation problem at once, so one
 *     round of fixes is enough.
 *
 * "Vorlage herunterladen" hands out the current model in exactly the upload
 * format — the intended way to start a file, since it round-trips unchanged.
 */

import { useRef, useState, type ReactNode } from 'react';

import { errorMessage, exportSchemaFile, uploadSchemaFile } from '../../lib/api';
import { useToast } from '../../components/Toast';
import type { SchemaUploadResponse } from '../../lib/types';
import OperationCard from './OperationCard';

export interface SchemaFileCardProps {
  /** Non-null disables every control and explains why (native tooltip). */
  disabledReason?: string | null;
  /** Called after a run with the German summary and the server's error list. */
  onDone?: (message: string, errors: readonly string[]) => void;
}

const ACCEPT = '.yaml,.yml,.json';

export function SchemaFileCard({
  disabledReason = null,
  onDone,
}: SchemaFileCardProps): ReactNode {
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [dryRun, setDryRun] = useState(true);
  const [exporting, setExporting] = useState(false);

  const locked = disabledReason !== null;

  /** Hand the current model over as a file the operator can edit and re-upload. */
  async function onExport(): Promise<void> {
    setExporting(true);
    try {
      const response = await exportSchemaFile();
      const url = URL.createObjectURL(new Blob([response.yaml], { type: 'application/yaml' }));
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = response.filename;
      anchor.click();
      URL.revokeObjectURL(url);
      toast.success(
        `Vorlage heruntergeladen: ${response.counts.classes} Klassen, ` +
          `${response.counts.relations} Relationen.`,
      );
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setExporting(false);
    }
  }

  const buttonReason = locked
    ? disabledReason
    : file === null
      ? 'Bitte zuerst eine Schema-Datei (YAML oder JSON) auswählen.'
      : null;

  return (
    <OperationCard<SchemaUploadResponse>
      title="Schema-Datei hochladen"
      description={
        <>
          Eigenes Datenmodell aus einer YAML- oder JSON-Datei anlegen: Klassen (Karten) mit
          Attributen, Relationen und Lookup-Listen. Vorhandenes wird nie umbenannt, geändert oder
          gelöscht. Die Formulare von AssetRadar folgen weiterhin dem eingebauten Modell.
        </>
      }
      buttonLabel={dryRun ? 'Datei prüfen (Vorschau)' : 'Schema provisionieren'}
      runningLabel={dryRun ? 'Prüfe…' : 'Provisioniere…'}
      disabledReason={buttonReason}
      run={() =>
        file === null
          ? Promise.reject(new Error('Keine Schema-Datei ausgewählt.'))
          : uploadSchemaFile(file, dryRun)
      }
      errorsOf={(response) => response.result.errors}
      onDone={(response, errors) => {
        const r = response.result;
        const verb = response.dry_run ? 'Vorschau' : 'Provisioniert';
        onDone?.(
          `${verb}: ${r.created_classes.length} Klassen, ${r.created_attributes.length} Attribute, ` +
            `${r.created_domains.length} Domains, ${r.created_lookups.length} Lookups.`,
          errors,
        );
      }}
      summarise={(response) => {
        const r = response.result;
        const c = response.counts;
        return (
          <>
            {response.dry_run ? (
              <strong>Vorschau, nichts geschrieben. </strong>
            ) : null}
            Datei: {c.classes} Klassen, {c.attributes} Attribute, {c.relations} Relationen,{' '}
            {c.lookups} Lookups.{' '}
            {response.dry_run ? 'Würde neu anlegen' : 'Neu angelegt'}: {r.created_classes.length}{' '}
            Klassen, {r.created_attributes.length} Attribute, {r.created_domains.length} Domains,{' '}
            {r.created_lookups.length} Lookups. Bereits vorhanden: {r.skipped_classes.length}{' '}
            Klassen, {r.skipped_attributes.length} Attribute, {r.skipped_domains.length} Domains,{' '}
            {r.skipped_lookups.length} Lookups.
            {response.warnings.length > 0 ? (
              <ul className="ops-schemafile-warn">
                {response.warnings.map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
            ) : null}
          </>
        );
      }}
    >
      <div className="ops-schemafile">
        <div className="ops-schemafile-pick">
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            hidden
            onChange={(event) => setFile(event.target.files?.[0] ?? null)}
          />
          <button
            type="button"
            className="ghost"
            onClick={() => inputRef.current?.click()}
            disabled={locked}
            title={disabledReason ?? undefined}
          >
            Datei wählen…
          </button>
          {file !== null ? (
            <span className="imp-file">{file.name}</span>
          ) : (
            <span className="muted">YAML oder JSON</span>
          )}
        </div>

        <label className="check">
          <input
            type="checkbox"
            checked={dryRun}
            onChange={(event) => setDryRun(event.target.checked)}
            disabled={locked}
          />
          Nur prüfen (Vorschau, ohne in CMDBuild zu schreiben)
        </label>

        <div className="ops-schemafile-tpl">
          <button
            type="button"
            className="ghost"
            onClick={() => void onExport()}
            disabled={locked || exporting}
            title={disabledReason ?? undefined}
          >
            {exporting ? 'Exportiere…' : 'Vorlage herunterladen'}
          </button>
          <span className="muted">
            Das aktuelle Modell im Upload-Format, als Ausgangspunkt für die eigene Datei.
          </span>
        </div>

        <details className="ops-json">
          <summary>Dateiformat</summary>
          <pre>{`version: 1
classes:                       # oder: cards
  - name: Lieferant            # Klassenname (Buchstaben, Ziffern, _)
    description: Lieferant eines Bauteils
    parent: null               # optional: Elternklasse
    attributes:
      - name: Name
        type: string           # string text integer decimal double boolean
        description: Firmenname #  date dateTime reference lookup
        unique: true           # weitere Schlüssel gehen an CMDBuild
      - name: Status
        type: lookup
        lookup_type: Lieferant - Status
      - name: KomponenteTypeID
        type: reference
        target_class: KomponenteType     # Klasse, auf die verwiesen wird
        domain: LieferantKomponenteType  # Relation, über die verwiesen wird
relations:                     # oder: domains
  - name: LieferantKomponenteType
    source: Lieferant
    destination: KomponenteType
    cardinality: 1:N           # 1:1 1:N N:1 N:N
    label: liefert
    inverse_label: geliefert von
lookups:
  - type: Lieferant - Status
    values:
      - code: AKTIV
        description: Aktiv`}</pre>
        </details>
      </div>
    </OperationCard>
  );
}

export default SchemaFileCard;
